import React, {FunctionComponent, useMemo} from "react";
import {KeyboardArrowLeftRounded} from "@mui/icons-material";
import {Box, Divider, IconButton, Tooltip, Typography} from "@mui/material";
import {Link, useRouteMatch} from "react-router-dom";
import {renderPanelSidebarMenuItems} from "../index";
import {SidebarContent, usePanel} from "../../../layout";
import {routes} from "../../../../../core/models/static/routes/app-routes";
import useAccessLevels from "../../../../hooks/use-access-levels";
import {useAppSelector} from "../../../../redux";
import {useTranslation} from "react-i18next";
import type {CommandSearchScope} from "../../../panel-command-search";
import {createVendorPanelEntries, panelEntriesToSidebarItems} from "../../../../panel-entries";

const VENDOR_BACK_ENTRY_ID = 'vendor-back-to-vendors';

const PanelSidebarVendorContent: FunctionComponent = () => {
    const {params} = useRouteMatch<{ vendorId: string }>(routes.vendor.base) ?? {};
    const operatorAccessLevels = useAccessLevels();
    const panel = usePanel();
    const vendorInformation = useAppSelector(state => state.vendorInformation);
    const {t} = useTranslation("components", {keyPrefix: 'panel-sections.sidebar.vendor'});
    const {t: tCmd} = useTranslation("components", {keyPrefix: 'panel-sections.command-search.pages'});

    const vendorId = useMemo(() => params?.vendorId ?? '0', [params?.vendorId]);

    const entries = useMemo(
        () => createVendorPanelEntries({t, tCmd, packages: operatorAccessLevels.packages}),
        [t, tCmd, operatorAccessLevels.packages],
    );

    const scope = useMemo<CommandSearchScope>(() => ({
        panelId: 'main',
        stack: [{type: 'vendor', id: vendorId}],
    }), [vendorId]);

    const canAccess = useMemo(() => (accessKey: string) => {
        const [pkg, flag] = accessKey.split('.');
        return Boolean((operatorAccessLevels.packages as unknown as Record<string, Record<string, unknown>>)?.[pkg]?.[flag]);
    }, [operatorAccessLevels.packages]);

    const items = useMemo(
        () => panelEntriesToSidebarItems(entries, scope, {canAccess, panelEntries: entries})
            .filter((item) => item.key !== VENDOR_BACK_ENTRY_ID),
        [entries, scope, canAccess],
    );

    const content = useMemo(() => renderPanelSidebarMenuItems(items), [items]);

    return (
        <>
            <SidebarContent>
                <Divider sx={{marginInlineEnd: 2}}/>
                <Box sx={{display: "flex", alignItems: 'center', gap: 1}}>
                    <Tooltip title={t(tt => tt.backToMainSidebar)}>
                        <IconButton
                            sx={{
                                marginBlock: 2,
                                marginInline: panel.sidebarCollapsed ? 1 : 0,
                                '&:hover': {
                                    color: 'primary.main',
                                },
                            }}
                            component={Link as any}
                            to={routes.vendors}
                            replace
                        >
                            <KeyboardArrowLeftRounded/>
                        </IconButton>
                    </Tooltip>
                    {
                        !panel.sidebarCollapsed &&
                        <Typography variant={'body2'} textOverflow={'ellipsis'} noWrap>
                            {t(tt => tt.title, {name: vendorInformation?.name ?? t(tt => tt.vendorFallbackName)})}
                        </Typography>
                    }
                </Box>
                {content}
            </SidebarContent>
        </>
    );
};

export default PanelSidebarVendorContent;
